import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { useToast } from "../components/common/Toast";
import { createCheckoutSession, STRIPE_PLANS, formatPrice } from "../services/stripeService";
import { getPlanLimits } from "../utils/planLimits";
import { ROLES, STATUSES } from "../constants/roles";

export default function SubscriptionRequired() {
  const { user, userDoc, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(null);
  const [hasRedirected, setHasRedirected] = useState(false); // Circuit breaker

  // Redirect to dashboard if user has active trial or subscription
  useEffect(() => {
    if (authLoading) return; // Wait for auth to load
    if (!userDoc) return; // No user doc yet
    if (hasRedirected) return; // Prevent infinite loops

    // CRITICAL: Master admin should never see subscription page
    const userRole = (userDoc.role || "").trim();
    const userStatus = (userDoc.status || "").trim();

    if (userRole === ROLES.MASTER && userStatus === STATUSES.ACTIVE) {
      console.log('⚠️ SubscriptionRequired: Master admin should not be here, redirecting to /master-admin');
      setHasRedirected(true);
      navigate('/master-admin', { replace: true });
      return;
    }

    const limits = getPlanLimits(userDoc);

    console.log('SubscriptionRequired - User plan check:', {
      plan: userDoc.plan,
      expired: limits.expired,
      trialEndsAt: userDoc.trialEndsAt,
      trialEndsAtMs: limits.trialEndsAtMs,
      isActive: userDoc.isActive,
      now: Date.now()
    });

    // ✅ RE-ENABLED: Redirect users with active subscriptions to dashboard
    // Fixed infinite loop by checking subscription status BEFORE trial expiration
    const hasPaidSubscription = ['monthly', 'yearly'].includes(userDoc.plan);
    const hasActiveTrial = !limits.expired && userDoc.plan === 'trial';

    if (hasPaidSubscription || hasActiveTrial) {
      console.log('SubscriptionRequired: User has active plan, redirecting to dashboard', {
        plan: userDoc.plan,
        hasPaidSubscription,
        hasActiveTrial
      });
      setHasRedirected(true); // Set circuit breaker
      navigate('/dashboard', { replace: true });
    }
  }, [userDoc, authLoading, navigate, hasRedirected]);

  // Get plans from Stripe service
  const monthlyPlan = STRIPE_PLANS.find(p => p.interval === 'month');
  const yearlyPlan = STRIPE_PLANS.find(p => p.interval === 'year');

  const trialEndDate = userDoc?.trialEndsAt?.toDate
    ? userDoc.trialEndsAt.toDate()
    : userDoc?.trialEndsAt
    ? new Date(userDoc.trialEndsAt)
    : null;

  const handleUpgrade = async (priceId) => {
    if (!user?.uid || !user?.email) {
      toast.error('Please log in to subscribe');
      return;
    }

    try {
      setLoading(priceId);

      const sessionUrl = await createCheckoutSession({
        priceId,
        userId: user.uid,
        email: user.email
      });

      window.location.href = sessionUrl;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-sky-100 flex items-center justify-center px-4">
      <div className="bg-white rounded-lg shadow-2xl p-8 w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">⏰</div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Trial Expired</h1>
          <p className="text-gray-600">
            {trialEndDate
              ? `Your 14-day free trial ended on ${trialEndDate.toLocaleDateString()}`
              : "Your free trial has expired"}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Upgrade to continue creating quizzes and managing students
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Monthly Plan */}
          {monthlyPlan && (
            <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-blue-400 transition-colors">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{monthlyPlan.name}</h3>
                <div className="text-3xl font-bold text-blue-600 mb-1">
                  {formatPrice(monthlyPlan.price)}
                  <span className="text-sm font-normal text-gray-500">/{monthlyPlan.interval}</span>
                </div>
                <p className="text-sm text-gray-600 mb-4">Perfect for getting started</p>

                <button
                  onClick={() => handleUpgrade(monthlyPlan.id)}
                  disabled={loading === monthlyPlan.id}
                  className="w-full bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-700 hover:to-sky-700 text-white py-3 px-4 rounded-lg font-semibold transition duration-200 disabled:opacity-50"
                >
                  {loading === monthlyPlan.id ? "Processing..." : `Choose ${monthlyPlan.name}`}
                </button>
              </div>
            </div>
          )}

          {/* Yearly Plan */}
          {yearlyPlan && (
            <div className="border-2 border-blue-400 bg-blue-50 rounded-lg p-6 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  {yearlyPlan.popular ? 'MOST POPULAR' : 'SAVE MONEY'}
                </span>
              </div>

              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{yearlyPlan.name}</h3>
                <div className="text-3xl font-bold text-blue-600 mb-1">
                  {formatPrice(yearlyPlan.price)}
                  <span className="text-sm font-normal text-gray-500">/{yearlyPlan.interval}</span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  {formatPrice(Math.round(yearlyPlan.price / 12))}/month billed annually
                </p>

                <button
                  onClick={() => handleUpgrade(yearlyPlan.id)}
                  disabled={loading === yearlyPlan.id}
                  className="w-full bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-700 hover:to-sky-700 text-white py-3 px-4 rounded-lg font-semibold transition duration-200 disabled:opacity-50"
                >
                  {loading === yearlyPlan.id ? "Processing..." : `Choose ${yearlyPlan.name}`}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-blue-800 mb-2">✨ Full Access Includes:</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Unlimited quiz creation</li>
            <li>• Unlimited student accounts</li>
            <li>• Real-time exam monitoring</li>
            <li>• Advanced security features</li>
            <li>• Analytics and reporting</li>
            <li>• Priority support</li>
          </ul>
        </div>

        <div className="text-center space-y-2">
          <p className="text-sm text-gray-600">
            Questions? <a href="mailto:info@examlock.ai" className="text-blue-600 hover:text-blue-800">Contact us</a>
          </p>
        </div>
      </div>
    </div>
  );
}